import React, { useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Col, Row } from "react-bootstrap";
import logo from "../../Assets/Images/landing/eww-logo.png";
import "../../Assets/Styles/pages/header.scss";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import helper_functions from "../../Config/helper_functions";
import { message } from "antd";
import {
  setCreate,
  setTemplateReady,
} from "../../Redux/DownloadSlice/downloadSlice";
import {
  setTemplateForImage,
  skipBatchUrlImage,
  skipBatchUrlImageOne,
  setBatchTileForAll,
  setBatchTileEditPending,
} from "../../Redux/MainImage/imageSlice";
import { setTemplateBatch } from "../../Redux/Templates/templateSlice";
import {
  setCreate_Template,
  setOnline,
  setSavingExistingImageAsTemplate,
  setTemplate_Edit,
  setTemplateAuthoringAspectPreset,
} from "../../Redux/UserSlice/userSlice";
import { buildCreateTemplateTileRootFields } from "../../Utils/createTemplateTilePayload";
import { getScaledFontSizeWithRespectToTargetScale } from "../Canvas/canvasUtils";
import { setActiveId } from "../../Redux/WaterMarks/watermarks";
import { CreateTemplate } from "../../actions/Template/Template";
import DownloadImage from "../DownloadImage/downloadImage";
import NoInternetModal from "../NoInternetModal/noInternetModal";
import ProceedTemplate from "../ProceedTemplate/proceedTemplate";
import Profile from "../Profile/profile";
import SaveModal from "../SaveModal/saveModal";
import SettingsModal from "../SettingsModal/settingsModal";
import SignIn from "../SignIn/signIn";
import ButtonRounded from "../Signature/button";
import TemplateProgress from "../TemplateProgress/templateProgress";
import TemplateResponse from "../TemplateResponse/templateResponse";
import HeaderMobileView from "./HeaderMobileView";
import NewNameModal from "./NewNameModal";
import {
  canSaveSquareTemplate,
  getNextTemplateName,
  getVisibleImageFrame,
  squareThumbnailPreview,
} from "./headerUtils";
import type { RootState } from "../../Redux/store";

interface HeaderProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ isMenuOpen, setIsMenuOpen }) => {
  const [open, setOpen] = useState(false);
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openProceedTempalte, setOpenProceedTempalte] = useState(false);
  const [openProgress, setOpenProgress] = useState(false);
  const [batchindex, setBatchindex] = useState(null);
  const [loader, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [save, setSave] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [respTemplate, setResponse] = useState(false);
  // EWW-409/440: whether THIS particular save should keep the user on the current canvas once
  // the "Saved!" confirmation closes, instead of clearing it (which empties `mainImage.url` and
  // trips useEditLogic's redirect to /home). Captured at the moment the save succeeds rather
  // than read live off Redux by TemplateResponse later, because the flags it depends on are
  // flipped in that same synchronous block -- by the time the confirmation's 2s timeout fires
  // they no longer describe the flow that produced it.
  //
  // Covers all three save flows: single-image "Save as Template"
  // (`savingExistingImageAsTemplate`), Create Template (`create_Template`) and update-existing
  // (`edit_Template`) -- see the comment at the assignment for the constraint they share.
  const [keepCanvasOnSaveDone, setKeepCanvasOnSaveDone] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [retryingConnection, setRetryingConnection] = useState(false);
  const [newTempName, setNewTempname] = useState("");
  const [newNameModal, setNewNameModal] = useState(false);
  // Prevent duplicate template creation when downloadState.Image updates multiple times
  // while templateReady remains true (e.g. canvas capture runs more than once).
  const creatingTemplateRef = useRef(false);

  const headerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const history = useHistory();

  const file = useSelector((state: RootState) => state.mainImage);
  const mainImageState = useSelector((state: RootState) => state.mainImage);
  const waterMarksState = useSelector((state: RootState) => state.watermarks);
  const downloadState = useSelector((state: RootState) => state.downloadSlice);
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  // dispatch(setLoader(false))

  const ensureSquareTemplateSave = () => {
    // Template encryption uses the editor canvas (and remapped 1:1 frame for authoring),
    // not raw upload pixels. Allow save whenever the canvas is square — e.g. normal
    // single-image watermarking — even if the source file is not 1:1.
    if (userSliceState.create_Template || userSliceState.edit_Template) {
      return true;
    }
    const cw = Number(userSliceState.canvasSize?.width ?? 0);
    const ch = Number(userSliceState.canvasSize?.height ?? 0);
    const canvasIsSquare =
      cw > 0 && ch > 0 && Math.abs(cw / ch - 1) <= 0.02;
    if (canvasIsSquare) {
      return true;
    }
    const isSquare = canSaveSquareTemplate(
      downloadState.mainImageWidth,
      downloadState.mainImageHeight
    );
    if (!isSquare) {
      message.warning(
        "Template save supports only 1:1 images. Please use a square image."
      );
    }
    return isSquare;
  };

  /**
   * Before flipping `templateReady`, force the canvas back to a 1:1 preset so the save
   * effect (`dataWrapperEncrypt` + `flexible_frame` math) reads a genuinely square
   * `canvasSize` and watermarks are re-aligned to that square - identical to the legacy
   * flow when users had to upload a square image. Does nothing when the preset is
   * already 1:1 or when we're not authoring a template.
   */
  const remapToSquareAndSave = async () => {
    const isAuthoring =
      userSliceState.create_Template || userSliceState.edit_Template;
    const currentPreset = userSliceState.templateAuthoringAspectPreset;
    if (isAuthoring && currentPreset !== "1:1") {
      dispatch(setTemplateAuthoringAspectPreset("1:1"));
      // Two rAFs + a short buffer cover: (1) canvas resize effect dispatching
      // setCanvasSize, (2) useEffect recomputing canvasImageSize (contain fit),
      // (3) watermark components re-running alignment and writing new %-coords
      // back to Redux, (4) updateWatermarks pushing fresh pixel coords into
      // local state. 350ms is conservative but keeps the UI responsive.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setTimeout(resolve, 350));
        });
      });
    }
    dispatch(setCreate(true));
    dispatch(setTemplateReady(true));
  };

  const saveAsNew = () => {
    if (!ensureSquareTemplateSave()) return;
    if (Object.keys(userSliceState.user_Auth).length) {
      if (waterMarksState.items.length) {
        // EWW-409: mark this as "saving the currently open image" so the save confirmation
        // keeps the user on the canvas instead of navigating to /home. Navigation-only now --
        // see the flag's own comment in userSlice.ts and canvas.tsx's `templateAspectAuthoring`
        // for why it must NOT also change the resize/encode-reference geometry (that broke the
        // saved template's own preview, EWW-440).
        //
        // Guarded on NOT already authoring: this same `saveAsNew` is the completion handler for
        // BOTH entry points -- the single-image "Save as Template" button AND the Create
        // Template / update-template button (both open `NewNameModal`, whose `onComplete` is
        // this). Setting the flag unconditionally therefore also marked a genuine authoring
        // session as "saving an existing image", which made the post-save branch drop
        // `create_Template` and snap the ratio-bar canvas back to the single-image view.
        // `create_Template` is switched on a few lines below, so this reads the pre-click state.
        if (!(userSliceState.create_Template || userSliceState.edit_Template)) {
          dispatch(setSavingExistingImageAsTemplate(true));
        }
        // Entering template-save mode turns on `create_Template`, which otherwise sizes
        // the canvas from `templateAuthoringAspectPreset` (possibly non-square). Force
        // 1:1 first so values stay aligned with the legacy square save path, then
        // `remapToSquareAndSave` can settle layout before encrypt.
        dispatch(setTemplateAuthoringAspectPreset("1:1"));
        dispatch(setCreate_Template(true));
        setNewNameModal(false);
        setOpenTemplate(true);
      } else {
        message.error(t("NoWaterMarksTemplateError"));
        console.log("openProgress", openProgress);
      }
    } else {
      setOpenSignIn(true);
    }
  };

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(true);
      dispatch(setOnline(true));
    };
    const handleOfflineStatus = () => {
      setIsOnline(false);
      dispatch(setOnline(false));
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
    };
  }, [navigator.onLine]);

  // The "No internet" modal normally closes on its own when the browser fires `online`, but
  // that event is unreliable on some setups (flaky links, VMs). Retry forces a fresh check:
  // show the spinner, actually try to reach the API, and only drop the modal on success --
  // otherwise it just falls back to the Retry button so the user can try again.
  const handleRetryConnection = async () => {
    if (retryingConnection) return;
    setRetryingConnection(true);
    let reachable = false;
    if (navigator.onLine) {
      try {
        // `navigator.onLine` only means an interface is up, not that the internet/API is
        // actually reachable -- confirm with a real request. `no-cors` is fine: we only care
        // whether it resolves (reachable) or rejects (not), not about the response body.
        await fetch(process.env.REACT_APP_API_URL || "/", {
          method: "HEAD",
          mode: "no-cors",
          cache: "no-store",
        });
        reachable = true;
      } catch {
        reachable = false;
      }
    }
    // Keep the spinner up a beat so a near-instant failure doesn't just flash.
    await new Promise((r) => setTimeout(r, 500));
    setRetryingConnection(false);
    if (reachable) {
      setIsOnline(true);
      dispatch(setOnline(true));
    }
  };

  const condition = () => {
    if (location.pathname === "/edit") {
      if (file.batchurls.length > 0) return headerChoice3();
      else return headerChoice2();
    } else if (
      location.pathname === "/home" ||
      location.pathname === "/addtemplate" ||
      location.pathname === "/batch-watermark" ||
      location.pathname === "/" ||
      location.pathname === "/features" ||
      location.pathname === "/pricing" ||
      location.pathname === "/faq" ||
      location.pathname === "/blogs" ||
      location.pathname.startsWith("/blogs/") ||
      location.pathname === "/profile" ||
      location.pathname === "/terms" ||
      location.pathname === "/refund-policy" ||
      location.pathname === "/privacy-policy"
    ) return null; // Home/AddTemplate/Landing/Features/Pricing/FAQ/Blogs/Profile/Terms/Refund/Privacy pages render their own desktop nav layout (mobile still falls through to HeaderMobileView below).
    else return headerChoice4();
  };
  const handleHome = async () => {
    history.push("/");
  };
  const handlePage = (page: string) => {
    setIsMenuOpen(false);
    history.push(page);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCompleteEdit = (all: boolean) => {
    const id = Date.now();
    let index = batchindex;
    if (all) {
      index = -1;
      dispatch(setBatchTileForAll(userSliceState.createTemplateTilePattern1));
      // EWW-458: this is a real Apply-to-all decision, so clear the pending flag the same way
      // `applyTileToAllBatchImages` does -- otherwise it's left dangling (harmless here since
      // `setTemplate_Edit(false)` below ends the batch-editing session anyway, but there's no
      // reason to leave stale state behind).
      dispatch(setBatchTileEditPending(false));
    }
    dispatch(
      setTemplateForImage({
        index: index,
        tempID: String(id),
      })
    );
    const temp = {
      id: String(id),
      watermarks: waterMarksState.items,
    };
    dispatch(setTemplate_Edit(false));
    dispatch(setTemplateBatch(temp));
    setSave(false);
  };

  const handleSkip = () => {
    if (file.batchurls.length > 1) {
      if (batchindex !== 0) {
        dispatch(skipBatchUrlImage(batchindex));
      } else {
        dispatch(skipBatchUrlImageOne());
      }
    } else if (file.batchurls.length === 1) {
      history.push("/");
    } else {
      alert("Cannot skip only one image.");
    }
  };

  useEffect(() => {
    setEdit(userSliceState.template_Edit);
  }, [userSliceState.template_Edit]);
  useEffect(() => {
    if (file.batchurls.length > 0) {
      setBatchindex(file.currentIndex);
    }
  }, [file.currentIndex]);

  useEffect(() => {
    if (loader) setOpen(true);
  }, [loader]);

  useEffect(() => {
    const run = async () => {
      // Wait for canvas export to finish (Create=false). If we run while Create is still true,
      // templateReady gets cleared before runExport reads it — export falls back to full stage
      // (base + watermarks) and/or this effect uses a stale composite Image.
      if (
        downloadState.Image &&
        downloadState.templateReady &&
        !downloadState.Create
      ) {
        if (creatingTemplateRef.current) return;
        creatingTemplateRef.current = true;
        try {
          const sourcesArray = [];
          // Match admin payload: include ALL watermarks (no dedupe/filter)
          const watermarks = waterMarksState.items ?? [];
          const orderedArray: Array<Record<string, unknown>> = [];
          const usedServerIds = new Set<string>();
          const tileDraft = userSliceState.createTemplateTilePattern1;
          const tiledActive =
            tileDraft.tiledEnabled &&
            Boolean(tileDraft.tilePatternId) &&
            tileDraft.tilePatternId !== "none";

          for (let i = 0; i < watermarks.length; i++) {
            let tempTemplate: Record<string, unknown> = {};
            let data: Record<string, unknown>;
            let wmImagePixelDims: { w: number; h: number } | null = null;
            // Match admin: keep stable `localId` while editing an existing template.
            // API requires localId to be a valid UUID; editor may use numeric ids (Date.now()).
            const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
            const candidate: string | null =
              typeof watermarks[i]?.localId === "string" && watermarks[i]?.localId
                ? (watermarks[i].localId as string)
                : typeof watermarks[i]?.id === "string" && watermarks[i]?.id
                  ? (watermarks[i].id as string)
                  : null;
            const waterMarksId = (candidate && typeof candidate === "string" && isUuid(candidate)) ? candidate : uuidv4();
            // Include all watermark types that have media (image, signature, logo, sticker).
            // When updating a template, existing watermarks must be in sourcesArray so their
            // media is re-uploaded; otherwise the backend may drop them.
            // Match admin: include "file" (generic image) and all media types
            const hasMedia =
              watermarks[i].type === "image" ||
              watermarks[i].type === "file" ||
              watermarks[i].type === "signature" ||
              watermarks[i].type === "logo" ||
              watermarks[i].type === "sticker";
            if (hasMedia) {
              const item = { ...watermarks[i] } as Record<string, unknown>;
              let src: string | File | Blob = (item.src as string) ?? (item.src as File) ?? (item.file as File) ?? "";
              // Resolve blob URLs, http(s) URLs, and relative paths to File (Template expects data URL string or File/Blob)
              if (typeof src === "string" && !src.startsWith("data:")) {
                try {
                  src = await helper_functions.WebToFile(src);
                } catch (e) {
                  console.warn("Failed to fetch image URL for watermark:", e);
                }
              }
              // If it's an SVG file, convert to high-res PNG like we do elsewhere
              if (src instanceof File && helper_functions.isSvgFile(src)) {
                try {
                  src = await helper_functions.convertSvgFileToHighResPng(src, 4);
                } catch (e) {
                  console.warn("Failed to convert SVG to PNG:", e);
                }
              }
              data = { ...item, id: waterMarksId };
              delete data.src;
              delete data.file;
              // Ensure each image watermark has a unique persisted name (avoid duplicates like "main-design.png").
              const ts = Date.now();
              const uniqueName = `wmImage${ts}${i ? `_${i}` : ""}.png`;
              // Update size to reflect the actual file size if available
              const fileSize = src instanceof File || src instanceof Blob ? src.size : (item.size as number) || 0;
              data = { ...data, name: uniqueName, size: fileSize };
              // Only add to sources when we have uploadable content (existing serverId watermarks
              // without src are kept by backend; new ones without src would fail upload).
              // Match admin: ALWAYS add media watermarks to sources so backend receives uploads.
              // Fallback to mediaUrl when src is lost (e.g. File GC'd) - re-fetch from CDN.
              let effectiveSrc: string | File | Blob = src;
              const canUpload =
                (typeof effectiveSrc === "string" && effectiveSrc.length > 0) ||
                (effectiveSrc instanceof File && effectiveSrc.size > 0) ||
                (effectiveSrc instanceof Blob && effectiveSrc.size > 0);
              if (!canUpload) {
                const mediaUrl = (item.mediaUrl ?? watermarks[i].mediaUrl) as string | undefined;
                if (mediaUrl && typeof mediaUrl === "string" && mediaUrl.startsWith("http")) {
                  try {
                    effectiveSrc = await helper_functions.WebToFile(mediaUrl);
                  } catch (e) {
                    console.warn("Failed to fetch mediaUrl for watermark:", e);
                  }
                }
              }
              const canUploadFinal =
                (typeof effectiveSrc === "string" && effectiveSrc.length > 0) ||
                (effectiveSrc instanceof File && effectiveSrc.size > 0) ||
                (effectiveSrc instanceof Blob && effectiveSrc.size > 0);
              if (canUploadFinal) {
                sourcesArray.push({
                  local_id: waterMarksId,
                  name: uniqueName,
                  src: effectiveSrc,
                });
                if (tiledActive) {
                  wmImagePixelDims = await helper_functions.getPixelSizeFromBlobOrDataUrl(effectiveSrc);
                }
              }
              tempTemplate = {
                ...tempTemplate,
                media: {
                  type: watermarks[i].property ?? watermarks[i].type ?? "image",
                  name: uniqueName,
                  bytes: watermarks[i].size ?? 0,
                },
              };
            } else data = watermarks[i];

            // Align with admin: persisted watermark JSON `id` should be the watermark UUID (localId),
            // not the runtime numeric id used by the editor.
            data = { ...(data || {}), id: waterMarksId } as Record<string, unknown> & { id: string };


            const canvasW = userSliceState.canvasSize?.width ?? 651;
            const canvasH = userSliceState.canvasSize?.height ?? 651;
            const visibleImageFrame = getVisibleImageFrame(
              userSliceState.imageFrame,
              canvasW,
              canvasH
            );
            const isTemplateAuthoring =
              (userSliceState.create_Template || userSliceState.edit_Template) &&
              // EWW-435/453: saving the CURRENTLY OPEN image encodes against the photo, not the
              // canvas -- `create_Template` is switched on by `saveAsNew` at the moment of
              // saving, so without this the reference flips at save time to a frame the user
              // never positioned against. Matches iOS, where `getImageFrameInScreen` uses the
              // canvas frame only for `.eCreateTemplate`/`.eEditTemplate`.
              !userSliceState.savingExistingImageAsTemplate;

            const imageSize = isTemplateAuthoring
              ? {
                // Template authoring: we already remapped the canvas to 1:1 before arriving
                // here (see `remapToSquareAndSave`), so treat the square canvas itself as the
                // reference "image". This matches the legacy mandatory-square flow exactly,
                // regardless of the actual uploaded image dimensions or cover-fit bleed.
                width: canvasW,
                height: canvasH,
                offsetX: 0,
                offsetY: 0,
              }
              : visibleImageFrame
                ? {
                  width: visibleImageFrame.width,
                  height: visibleImageFrame.height,
                  offsetX: visibleImageFrame.offsetX,
                  offsetY: visibleImageFrame.offsetY,
                }
                : {
                  // Fallback: previous behaviour (treat canvas as image)
                  width: canvasW,
                  height: canvasH,
                  offsetX: 0,
                  offsetY: 0,
                };

            // When tiling is on, the watermark isn't shown at its own saved size -- it's shrunk
            // to fit the tile pattern's cell size. Fake its containerWidth/Height (and scale, for
            // images) to that tile size before encoding, so the saved template records the size
            // actually seen on screen, not the pre-tile original.
            let dataForEncrypt: Record<string, unknown> = data;
            if (tiledActive) {
              const W = Math.max(1, imageSize.width);
              const H = Math.max(1, imageSize.height);
              const sz = Math.max(0.01, Math.min(1, tileDraft.size));
              if (data.type === "text") {
                const fs = getScaledFontSizeWithRespectToTargetScale(
                  String(data.src ?? "").trim() || " ",
                  String(data.font ?? "Arial"),
                  { width: W, height: H },
                  sz
                );
                dataForEncrypt = { ...data, containerWidth: Math.max(1, W * sz), containerHight: Math.max(1, fs * 1.35) };
              } else if (data.type === "image" || data.type === "signature") {
                const el = (data.image ?? watermarks[i].image) as HTMLImageElement | undefined;
                let iw = Number(el?.naturalWidth || el?.width) || 0;
                let ih = Number(el?.naturalHeight || el?.height) || 0;
                if ((iw <= 0 || ih <= 0) && wmImagePixelDims) {
                  iw = wmImagePixelDims.w;
                  ih = wmImagePixelDims.h;
                }
                const tilePx = Math.max(1, W * sz);
                const ok = iw > 0 && ih > 0;
                const s = ok ? tilePx / Math.max(iw, ih) : 1;
                dataForEncrypt = {
                  ...data,
                  containerWidth: ok ? iw : tilePx,
                  containerHight: ok ? ih : tilePx,
                  scaleX: s,
                  scaleY: s,
                };
              }
            }

            const encrypted = await helper_functions.dataWrapperEncrypt(
              dataForEncrypt,
              i,
              canvasW,
              imageSize
            );
            data = { ...encrypted, id: waterMarksId };

            // Build watermark payload to match Android: type, data, localId, serverId (only for existing).
            // Do NOT spread tempTemplate - text watermarks must never inherit "media" from previous iteration.
            const itemPayload: Record<string, unknown> = {
              type: data.type,
              data: JSON.stringify(data),
              localId: waterMarksId,
            };
            if (hasMedia && tempTemplate?.media) {
              itemPayload.media = tempTemplate.media;
            }
            const sid = watermarks[i].serverId as string | undefined;
            if (
              userSliceState.edit_Template &&
              userSliceState.current_TemplateID &&
              sid &&
              !usedServerIds.has(sid)
            ) {
              usedServerIds.add(sid);
              itemPayload.serverId = sid;
            }

            orderedArray.push(itemPayload);
          }

          // dispatch(setCreate_Template(false));
          // dispatch(setEdit_Template(false));

          // EWW-440/453: saving an already-open image builds its preview the way iOS does --
          // the watermark layer cropped to the IMAGE area, at the image's own aspect, with no
          // square crop. Genuine template authoring passes nothing and keeps the 1:1 crop.
          const previewCanvasW = userSliceState.canvasSize?.width ?? 0;
          const previewCanvasH = userSliceState.canvasSize?.height ?? 0;
          const previewVisibleFrame = userSliceState.savingExistingImageAsTemplate
            ? getVisibleImageFrame(
              userSliceState.imageFrame,
              previewCanvasW,
              previewCanvasH
            )
            : null;
          const previewImageFrame = previewVisibleFrame
            ? {
              ...previewVisibleFrame,
              canvasWidth: previewCanvasW,
              canvasHeight: previewCanvasH,
            }
            : null;
          const squareLarge = await squareThumbnailPreview(
            downloadState,
            previewImageFrame
          );
          // Was hardcoded to 100x100, which looked blurry once shown larger anywhere in the
          // UI -- now sized from actual screen resolution (see getScreenAdjustedImageSize).
          const squareLargeDims = await helper_functions.getImageDimensionsFromDataUrl(
            squareLarge
          );
          const thumbSize = helper_functions.getScreenAdjustedImageSize(
            squareLargeDims.width,
            squareLargeDims.height,
            false
          );
          const img = await helper_functions.resizeFile(
            squareLarge,
            thumbSize.width,
            thumbSize.height,
            "png",
            10
          );
          const size_thumb = helper_functions.dataURLtoFile(img, "image.png").size;
          const size_large = helper_functions.dataURLtoFile(
            squareLarge,
            "image.png"
          ).size;
          let name;
          if (
            userSliceState?.edit_Template &&
            !userSliceState.create_Template &&
            userSliceState.template
          ) {
            const effectiveId =
              typeof userSliceState.current_TemplateID === "string"
                ? userSliceState.current_TemplateID
                : (userSliceState.current_TemplateID as { landcsapeTemp?: string; portraitTemp?: string })?.landcsapeTemp ||
                (userSliceState.current_TemplateID as { landcsapeTemp?: string; portraitTemp?: string })?.portraitTemp;
            const templateList = Array.isArray(userSliceState.template)
              ? userSliceState.template
              : [];
            const matched = templateList.filter(
              (t: { id: string }) => t.id === effectiveId
            );
            name = matched?.[0]?.name ?? getNextTemplateName();
          } else {
            name = newTempName.length > 0 ? newTempName : getNextTemplateName();
          }
          // Match admin: include canvasRatio for backend validation
          const canvasW = userSliceState.canvasSize?.width ?? 651;
          const canvasH = userSliceState.canvasSize?.height ?? 651;
          /**
           * EWW-453: `canvasRatio` must describe the SAME frame the watermark geometry was
           * encoded against, or the template is internally inconsistent and the apply side lays
           * everything out against a frame the numbers never referred to. Saving an already-open
           * image encodes against the visible image area, so report that area's ratio; genuine
           * authoring encodes against the canvas and keeps reporting the canvas ratio.
           */
          const ratioFrame =
            userSliceState.savingExistingImageAsTemplate &&
              getVisibleImageFrame(userSliceState.imageFrame, canvasW, canvasH)
              ? getVisibleImageFrame(userSliceState.imageFrame, canvasW, canvasH)!
              : { width: canvasW, height: canvasH, offsetX: 0, offsetY: 0 };
          const canvasRatio =
            Number.isFinite(ratioFrame.width) &&
              Number.isFinite(ratioFrame.height) &&
              ratioFrame.height > 0
              ? ratioFrame.width / ratioFrame.height
              : 1;
          const templateData = {
            id:
              userSliceState.current_TemplateID &&
                userSliceState.edit_Template &&
                !userSliceState.create_Template
                ? userSliceState.current_TemplateID
                : uuidv4(),
            name: name,
            platform: "web",
            patternType: "normal",
            orientation:
              mainImageState.orientation === "landscape" ? "landScape" : "portrait",
            templateType: "flexible",
            canvasRatio: Number.isFinite(canvasRatio) && canvasRatio > 0 ? canvasRatio : 1,
            media: {
              thumbSizeInBytes: size_thumb,
              originalSizeInBytes: size_large,
            },
            // "thumb_size_in_bytes": size_thumb,
            // "large_size_in_bytes": size_large,
            // "media_name": "media.png"
          };

          const templateDataWithTile = {
            ...templateData,
            ...buildCreateTemplateTileRootFields(
              userSliceState.createTemplateTilePattern1
            ),
          };

          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const rawDeletedIds = Array.isArray(waterMarksState.deletedWaterMarkIds)
            ? waterMarksState.deletedWaterMarkIds
            : [];
          const validDeletedIds = rawDeletedIds.filter(
            (id): id is string => typeof id === "string" && uuidRegex.test(id)
          );

          const templatePayload = {
            ...templateDataWithTile,
            watermarks: orderedArray,
            ...(userSliceState.edit_Template && userSliceState.current_TemplateID
              ? { deletedIds: validDeletedIds }
              : { deletedIds: [] }),
          };

          const resp = await dispatch(
            CreateTemplate(
              templatePayload as Parameters<typeof CreateTemplate>[0],
              squareLarge,
              img,
              sourcesArray,
              userSliceState
            )
          ) as unknown as boolean;
          // Capture BEFORE the mode flags below are flipped -- they are what this decides from.
          //
          // EWW-456: ONLY the single-image "Save as Template" flow stays on its canvas (the user
          // still has a watermarked photo to export). Both authoring flows -- Create Template and
          // updating an existing one -- deliberately let the canvas clear, which is what triggers
          // useEditLogic's redirect; that redirect sends them to /addtemplate (the template
          // section) because `create_Template`/`edit_Template` are still set when it fires.
          setKeepCanvasOnSaveDone(
            Boolean(userSliceState.savingExistingImageAsTemplate)
          );
          setResponse(Boolean(resp));
          setOpenTemplate(false);
          if (resp) {
            // Only the single-image "Save as Template" flow leaves authoring mode here: it only
            // switched `create_Template` on to reuse this save plumbing, so it must switch back
            // to land the user in the normal single-image editor.
            //
            // A genuine Create Template session must STAY in authoring mode. `create_Template`
            // is what `EditorCanvas`'s `templateAspectAuthoring` renders the ratio bar from and
            // what keeps the canvas in its authoring layout -- clearing it unconditionally is
            // what made the screen snap back to the single-image view the instant the save
            // finished, even though nothing had navigated.
            if (userSliceState.savingExistingImageAsTemplate) {
              dispatch(setCreate_Template(false));
            }
            // `savingExistingImageAsTemplate` is deliberately NOT reset here. TemplateResponse
            // reads it live to decide whether to keep the photo once its "Saved!" confirmation
            // closes (a couple of seconds from now); resetting it in this same tick would clear
            // it before that read happens. It's reset once that modal actually closes instead
            // (see the wrapped `setOpen` passed to it below).
            //
            // `setEdit_Template(false)` intentionally no longer fires here either: updating an
            // existing template now stays on its authoring canvas, exactly like Create Template.
            // Leaving edit mode on is what keeps the ratio bar mounted -- and it must match the
            // capture above, which keeps this flow's image, or the two disagree and the user is
            // left on /edit in the single-image view. `current_TemplateID` is untouched, so a
            // further save still updates the SAME template rather than creating a new one.
            // Route changes away from /edit still clear both flags (see App.tsx).
          } else {
            message.error(t("TemplateSaveFailed"));
          }
        } finally {
          creatingTemplateRef.current = false;
          dispatch(setTemplateReady(false));
        }
      }
    };
    void run();
  }, [downloadState.templateReady, downloadState.Image, downloadState.Create, dispatch]);

  const handleTemplate = () => {
    if (!ensureSquareTemplateSave()) {
      setOpenTemplate(false);
      return;
    }
    void remapToSquareAndSave();
  };

  const headerChoice2 = () => {
    return (
      <Row
        ref={headerRef}
        onClick={() => {
          dispatch(setActiveId(""));
        }}
        className="header landing_header edit-header-single m-0 d-flex bg-white "
      >
        <Col className="d-flex flex-column align-items-start justify-content-center ">
          <Row className="d-flex productSansBold text_22 justify-content-center align-items-center pointer flex-nowrap">
            <Col onClick={handleHome} className="p-0">
              <img alt="logo" src={logo} className="logo" />
            </Col>
            <Col onClick={handleHome} className="logo_text  mx-1  m-0 p-0">
              {" "}
              {t("lang.ezywatermark")}
            </Col>
          </Row>
        </Col>
        <Col className="d-flex flex-column align-items-end justify-content-center p-0 m-0">
          <Row className="d-flex p-0 m-0 text_15 productSansLight justify-content-center align-items-center flex-nowrap">
            {userSliceState.edit_Template && (
              <Col>
                <button
                  onClick={() => {
                    setNewNameModal(true);
                  }}
                  className="button_text w_edit_button m-0 py-1 bg_templ d-flex justify-content-center align-items-center button_text_signin"
                >
                  Save as New
                </button>
              </Col>
            )}
            {(userSliceState.edit_Template ||
              userSliceState.create_Template) && (
                <Col>
                  <button
                    onClick={() => {
                      if (!ensureSquareTemplateSave()) return;
                      if (Object.keys(userSliceState.user_Auth).length) {
                        if (waterMarksState.items.length) {
                          // Authoring flow (this button only renders while create/edit_Template
                          // is on already) -- keep the existing clear-and-go-home behaviour, in
                          // case a stale `true` is still hanging around from an earlier
                          // `saveAsNew` in this same mounted session.

                          if (userSliceState.create_Template) {
                            setNewNameModal(true);
                          } else {
                            setOpenTemplate(true);
                          }
                          // if (userSliceState.edit_Template) {
                          //   setTimeout(() => {
                          //     window.location.reload();
                          //   }, 10000);
                          // }
                        } else {
                          message.error(t("NoWaterMarksTemplateError"));
                        }
                      } else {
                        setOpenSignIn(true);
                      }
                    }}
                    className="button_text w_edit_button m-0 py-1  bg_templ d-flex justify-content-center align-items-center button_text_signin"
                  >
                    {t("UpdateTemplate")}
                  </button>
                </Col>
              )}
            {!(
              userSliceState.edit_Template || userSliceState.create_Template
            ) ? (
              <>
                {waterMarksState.items.length > 0 && (
                  <Col>
                    <button
                      className="button_text w_edit_button m-0 py-1  bg_export_outline rounded-rect-btn d-flex justify-content-center align-items-center gap-2 button_text_signin"
                      onClick={() => {
                        if (!ensureSquareTemplateSave()) return;
                        if (Object.keys(userSliceState.user_Auth).length) {
                          setNewNameModal(true);
                        } else {
                          setOpenSignIn(true);
                        }
                      }}
                    >
                      {loader ? (
                        <CircularProgress size={24} />
                      ) : (
                        <>{t("header.SaveTemplate")}</>
                      )}
                    </button>
                  </Col>
                )}
                <Col>
                  {/* Same "Proceed" action as before -- relabeled "Export" to match the updated
                      design; no handler change. */}
                  <button
                    className="button_text w_edit_button m-0 py-1  bg_proceed rounded-rect-btn d-flex justify-content-center align-items-center gap-2 button_text_signin"
                    onClick={() => {
                      if (waterMarksState.items.length) setLoading(true);
                      else {
                        if (window.confirm(t("NoWaterMarksProceedError")))
                          setLoading(false);
                      }
                    }}
                  >
                    {loader ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <FileDownloadOutlinedIcon style={{ fontSize: 20 }} />
                        Export
                      </>
                    )}
                  </button>
                </Col>
              </>
            ) : null}

            <Col className="p-0 m-0">
              {!Object.keys(userSliceState.user_Auth).length ? (
                <button
                  onClick={() => {
                    setOpenSignIn(true);
                  }}
                  className="button_text h-100 d-flex justify-content-center align-items-center button_text_signin w-100 px-4 py-1"
                >
                  {t("header.SignIn")}
                </button>
              ) : (
                <Profile setOpenSettings={setOpenSettings} anchor={headerRef} />
              )}
            </Col>
          </Row>
        </Col>
        <DownloadImage
          setLoaderParent={setLoading}
          open={open}
          setOpen={setOpen}
          setOpenProgress={setOpenProgress}
        />
        <NewNameModal
          open={newNameModal}
          setOpen={setNewNameModal}
          name={newTempName}
          setName={setNewTempname}
          onComplete={saveAsNew} // When user saves the name this function will be called
        />
        <ProceedTemplate
          open={openProceedTempalte}
          setOpen={setOpenProceedTempalte}
          setLoaderParent={() => { return; }}
          setOpenProgress={() => { return; }}
        />
        {/* <DownloadImageProgress
          open={openProgress}
          setOpenProgress={setOpenProgress}
          setOpen={setOpen}
        /> */}
        <SignIn open={openSignIn} setOpen={setOpenSignIn} />
        <SettingsModal
          open={openSettings}
          setOpen={setOpenSettings}
          handleDone={() => setOpenSettings(false)}
          parent={false}
        />
        <TemplateProgress
          open={openTemplate}
          handleTemplate={handleTemplate}
        />
        <TemplateResponse
          open={respTemplate}
          setOpen={(v) => {
            setResponse(v);
            // Reset once the confirmation modal actually finishes with it -- see the comment
            // where this flag is set to true (`saveAsNew`) for why it isn't reset any earlier.
            if (!v) dispatch(setSavingExistingImageAsTemplate(false));
          }}
          keepImageOnDone={keepCanvasOnSaveDone}
        />
      </Row>
    );
  };
  const headerChoice3 = () => {
    return (
      <Row
        ref={headerRef}
        onClick={() => {
          dispatch(setActiveId(""));
        }}
        className="header m-0 p-0 d-flex bg-white "
      >
        <Col className="d-flex flex-column align-items-start justify-content-center mx-3 px-2">
          <Row className="d-flex productSansBold text_22 justify-content-center align-items-center pointer flex-nowrap">
            <Col onClick={handleHome}>
              <img alt="logo" src={logo} className="logo" />
            </Col>
            <Col onClick={handleHome} className="logo_text mx-1  m-0 p-0">
              {" "}
              {t("lang.ezywatermark")}
            </Col>
          </Row>
        </Col>
        <Col>
          <Row className="d-flex text_18 productSansMedium justify-content-center align-items-center flex-nowrap h-100">
            {batchindex + 1 <= file.batchurls.length
              ? "Image " + (batchindex + 1) + " of " + file.batchurls.length
              : "Processing Done"}
          </Row>
        </Col>
        <Col className="d-flex flex-column align-items-end justify-content-center  mx-3 px-2">
          <Row className="d-flex text_19 productSansLight justify-content-center align-items-center flex-nowrap">
            {/* Prev/next batch nav buttons moved to Components/Canvas/canvas.tsx, pinned to
                the left/right edges of the image itself instead of living up here. */}
            {/* <Col className=" m-0  mx-2 p-0">
							<ButtonRounded
								onclick={handleTemplateClick}
								text={edit ? "Save" : t("Edit")}
								style={"w_edit_button bg_batch my-0 mb-0 p-0"}
								parrentStyle={""}
							/>
						</Col> */}
            {!edit ? (
              <Col className=" m-0 mx-2 p-0">
                <ButtonRounded
                  onclick={handleSkip}
                  text={"Skip"}
                  style={"w_edit_button bg_batch my-0 mb-0 p-0"}
                  parrentStyle={" "}
                />
              </Col>
            ) : null}
            <Col className="mx-2 m-0 p-0">
              <Profile setOpenSettings={setOpenSettings} anchor={headerRef} />
            </Col>
          </Row>
        </Col>
        <SaveModal
          open={save}
          handleCompleteEdit={handleCompleteEdit}
        />
        <SettingsModal
          open={openSettings}
          setOpen={setOpenSettings}
          handleDone={() => setOpenSettings(false)}
          parent={false}
        />
      </Row>
    );
  };
  const headerChoice4 = () => {
    return (
      <Row
        ref={headerRef}
        className="header m-0 d-flex bg-white landing_header border-bottom"
      >
        <Col className="d-flex flex-column align-items-start justify-content-center">
          <Row className="d-flex productSansBold text_22 justify-content-center align-items-center pointer">
            <Col className="p-0">
              <img
                alt="logo"
                onClick={handleHome}
                src={logo}
                className="logo"
              />
            </Col>
            <Col onClick={handleHome} className="logo_text mx-1 m-0 p-0">
              {" "}
              {t("lang.ezywatermark")}
            </Col>
          </Row>
        </Col>
        <Col className="d-flex flex-column align-items-end justify-content-center">
          <Row className="d-flex flex-row-reverse text_15 productSansLight text-black justify-content-end align-items-center flex-nowrap">
            <Col className="ms-4 p-0">
              {!Object.keys(userSliceState.user_Auth).length ? (
                <button
                  onClick={() => {
                    setOpenSignIn(true);
                  }}
                  className="button_text d-flex justify-content-center align-items-center button_text_signin w-100 px-4 py-3"
                >
                  {t("header.SignIn")}
                </button>
              ) : (
                <Profile setOpenSettings={setOpenSettings} anchor={headerRef} />
              )}
            </Col>
            {/* <Col className="ms-4 me-3 p-0">
              <button
                onClick={() =>
                  // (window.location.href = `${process.env.REACT_APP_GHOST_API_URL}`)
                  handlePage("/blogs")
                }
                className={` ${
                  location.pathname === "/blogs" && "highlightedHeaderText"
                }  button_text `}
              >
                {t("Blogs")}
              </button>
            </Col> */}
            <Col className="ms-4 me-3 p-0">
              <button
                onClick={() => handlePage("/faq")}
                className={` ${location.pathname === "/faq" && "highlightedHeaderText"
                  }  button_text `}
              >
                {t("FAQ")}
              </button>
            </Col>
            <Col className="ms-4 me-3 p-0">
              <button
                onClick={() => handlePage("/pricing")}
                className={` ${location.pathname === "/pricing" && "highlightedHeaderText"
                  }  button_text `}
              >
                {t("Pricing")}
              </button>
            </Col>
            <Col className="ms-4 me-3 p-0">
              <button
                onClick={() => handlePage("/features")}
                className={` ${location.pathname === "/features" && "highlightedHeaderText"
                  }  button_text `}
              >
                {t("Features")}
              </button>
            </Col>
          </Row>
        </Col>

        <SignIn open={openSignIn} setOpen={setOpenSignIn} />
        <SettingsModal
          open={openSettings}
          setOpen={setOpenSettings}
          handleDone={() => setOpenSettings(false)}
          parent={false}
        />
      </Row>
    );
  };
  return (
    <>
      <div className="d-lg-block d-none header">{condition()}</div>
      <div className="d-lg-none header">
        <HeaderMobileView
          handlePage={handlePage}
          setOpenSettings={setOpenSettings}
          setOpenSignIn={setOpenSignIn}
          handleMenuToggle={handleMenuToggle}
          setIsMenuOpen={setIsMenuOpen}
          isMenuOpen={isMenuOpen}
        />
        <NoInternetModal
          // Not shown during a batch export: that loop is fully client-side and finishes fine
          // offline, so the modal would just be a false alarm over its progress screen.
          open={!isOnline && !downloadState.batchExportInProgress}
          retrying={retryingConnection}
          onRetry={handleRetryConnection}
        />
      </div>
    </>
  );
};

export default Header;
